import React, { useCallback, useState, useEffect } from 'react'
import { ErrorCode, FileError, FileRejection, useDropzone } from 'react-dropzone'
import PlusIcon from '../../../icons/PlusIcon'
import { formatBytes } from '../../../../utils/helpers'
import ResetFileIcon from '../../../icons/ResetFileIcon'
import CircularProgress from '../../../CicularProgress'
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { isNativePlatform } from '../../../../utils/platformUtil'

interface FileInputProps {
    onChange: (file: File) => void
    maxSize?: number
    minSize?: number
    accept?: Record<string, string[]>
    progress?: number | null;
    capture?: string | null;
}

const FileInput: React.FC<FileInputProps> = ({ onChange, accept, maxSize, minSize, progress, capture }) => {
    const [preview, setPreview] = useState<string | null>(null)
    const [fileType, setFileType] = useState<string | null>(null)
    const [fileName, setFileName] = useState<string>('')
    const [errors, setErrors] = useState<FileError[]>([])

    const onDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            if (fileRejections.length > 0 && fileRejections[0]?.errors?.length > 0) {
                setErrors([...fileRejections[0].errors])
                return
            } else {
                setErrors([])
            }

            if (acceptedFiles.length > 0) {
                const file = acceptedFiles[0]
                onChange(file)
                setFileName(file.name)

                if (file.type.startsWith('image/')) {
                    const objectUrl = URL.createObjectURL(file)
                    setPreview(objectUrl)
                    setFileType('image')
                } else if (file.type === 'application/pdf') {
                    setPreview(null)
                    setFileType('pdf')
                } else if (file.type.startsWith('video/')) {
                    const objectUrl = URL.createObjectURL(file)
                    setPreview(objectUrl)
                    setFileType('video')
                }
            }
        },
        [onChange]
    )

    const handleClear = (e: any) => {
        e.stopPropagation();
        setPreview(null)
        setFileType(null)
        setFileName('')
        setErrors([])
        onChange(null as unknown as File)
    }

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview)
        }
    }, [preview])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        ...((accept && Object.keys(accept).length > 0) ? { accept } : {}),
        ...(maxSize ? { maxSize } : {}),
        ...(minSize ? { minSize } : {}),
        multiple: false,
        useFsAccessApi: false
    })

    // Handle camera capture natively
    const handleCameraCapture = async () => {
        try {
            const photo = await Camera.getPhoto({
                resultType: CameraResultType.Uri,
                source: CameraSource.Camera,
                quality: 90,
            });

            if (photo.webPath) {
                const response = await fetch(photo.webPath);
                const blob = await response.blob();
                const file = new File([blob], "photo.jpg", { type: blob.type });

                onChange(file);
                setFileName(file.name);
                setPreview(photo.webPath);
                setFileType("image");
            }
        } catch (err) {
            console.error("Camera error:", err);
        }
    };

    return (
        <>
            <div
                {...getRootProps()}
                className='form-builder-file-box cursor-pointer transition-colors duration-200 hover:bg-blue-100 text-center'
            >
                {
                    (capture && isNativePlatform()) ? null : (
                        <input {...getInputProps({ ...(capture ? { capture: capture as boolean | "user" | "environment" } : {}) })} />
                    )
                }

                {!preview && fileType !== 'pdf' ? (
                    (capture && isNativePlatform()) ? (
                        <button type="button" onClick={handleCameraCapture}>
                            <i className="fa-solid fa-camera"></i> Take Photo</button>
                    ) : (
                        <>
                            <span><PlusIcon /></span>
                            <span>{isDragActive ? 'Drop the file here...' : 'Upload File'}</span>
                        </>
                    )

                ) : (
                    <div className='preview-box'>
                        {fileType === 'image' && preview && (
                            <img src={preview} alt="preview" className="preview-image" />
                        )}
                        {fileType === 'pdf' && (
                            <div className='pdf-preview'>
                                📄 <span>{fileName}</span>
                            </div>
                        )}
                        {fileType === 'video' && preview && (
                            <video className='preview-video' src={preview} playsInline
                                muted
                                preload="metadata"
                                onLoadedMetadata={(e) => {
                                    e.currentTarget.currentTime = 0.1; // force thumbnail frame
                                }}>
                                Your browser does not support the video tag.
                            </video>
                        )}
                        <span onClick={handleClear} className='reset-file-icon'><ResetFileIcon /></span>
                    </div>
                )}
                {
                    progress &&
                    <div className="upload-progress">
                        <CircularProgress percentage={progress} />
                    </div>
                }
            </div>

            {errors.length > 0 &&
                errors.map((error, index) => (
                    <span className="text-danger d-block" key={index}>
                        {
                            error.code === ErrorCode.FileTooLarge ?
                                `File size should be less than or equal to ${formatBytes(maxSize)}`
                                : (error.code === ErrorCode.FileTooSmall ?
                                    `File size should be greater than or equal to ${formatBytes(minSize)}`
                                    : error.message
                                )
                        }
                    </span>
                ))
            }
        </>
    )
}

export default FileInput